import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogContent,
} from "@mui/material";
import {
  PlayCircleOutline as PlayIcon,
  Smartphone as MobileIcon,
  Computer as PcIcon,
  Close as CloseIcon,
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";
import { RichTextDisplay } from "@/components/common";
import { useGetCourseAdDetailQuery } from "@/store/api/homepageApi";

const CourseCard = ({ course }) => {
  const [videoOpen, setVideoOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  // Fetch full course details only when detail dialog is opened
  const {
    data: detailData,
    isLoading: isLoadingDetail,
    error: detailError,
  } = useGetCourseAdDetailQuery(course._id, { skip: !detailOpen });

  const courseDetail = detailData?.data || {};

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    );
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const embedUrl = getYouTubeEmbedUrl(course.sampleVideoUrl);
  const hasPrice = course.price !== null && course.price !== undefined;

  return (
    <>
      <Card
        sx={{
          // borderRadius: 3,
          background: "white",
          border: "1px solid rgba(0,0,0,0.08)",
          transition: "all 0.3s ease",
          overflow: "hidden",
          "&:hover": {
            boxShadow: "0 8px 30px rgba(33, 150, 243, 0.15)",
            transform: "translateY(-2px)",
          },
        }}
      >
        <CardContent sx={{ pt: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 0,
            }}
          >
            {/* Left: Course Info */}
            <Box sx={{ flex: 1, p: 2, pb: 1 }}>
              {/* Tags */}
              <Box sx={{ display: "flex", gap: 0.5, mb: 1, flexWrap: "wrap" }}>
                <Chip
                  label="BEST"
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    background: "#FF5722",
                    color: "white",
                  }}
                />
                <Chip
                  label="NEW"
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    background: "#4CAF50",
                    color: "white",
                  }}
                />
              </Box>

              {/* Title */}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#1a1a1a",
                  mb: 1,
                  lineHeight: 1.3,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {course.title}
              </Typography>

              {/* Teacher */}
              {course.teacherName && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  {course.teacherImage && (
                    <Box
                      component="img"
                      src={course.teacherImage}
                      alt={course.teacherName}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    sx={{ color: "#1976D2", fontWeight: 600 }}
                  >
                    {course.teacherName}
                  </Typography>
                </Box>
              )}

              {/* Description */}
              <Typography
                variant="body2"
                sx={{
                  color: "#666",
                  mb: 2,
                  lineHeight: 1.6,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {course.description}
              </Typography>

              {/* Meta info */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <MobileIcon sx={{ fontSize: 16, color: "#999" }} />
                  <Typography variant="caption" sx={{ color: "#999" }}>
                    Mobile
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PcIcon sx={{ fontSize: 16, color: "#999" }} />
                  <Typography variant="caption" sx={{ color: "#999" }}>
                    PC
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Center: Book Cover */}
            {course.coverImage && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  // p: 2,
                  minWidth: { xs: "auto", sm: 140 },
                }}
              >
                <Box
                  component="img"
                  src={course.coverImage}
                  alt={course.title}
                  sx={{
                    // width: { xs: 120, sm: 120 },
                    // height: { xs: 150, sm: 150 },
                    maxWidth: 150,
                    maxHeight: 130,
                    objectFit: "contain",
                    borderRadius: 2,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                />
              </Box>
            )}

            {/* Price Column */}
            {hasPrice && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                  minWidth: { xs: "auto", sm: 100 },
                  gap: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#999",
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Price
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: course.price === 0 ? "#4CAF50" : "#E91E63",
                    fontSize: "1rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {course.price === 0
                    ? "Free"
                    : `${course.price.toLocaleString("en-US")} \u20ab`}
                </Typography>
              </Box>
            )}

            {/* Right: Action Buttons */}
            {embedUrl && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                  gap: 1,
                  minWidth: { xs: "auto", sm: 140 },
                }}
              >
                {embedUrl && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PlayIcon />}
                    onClick={() => setVideoOpen(true)}
                    sx={{
                      color: "#1565C0",
                      borderColor: "#1565C0",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      textTransform: "none",
                      fontSize: "0.8rem",
                      width: "100%",
                      "&:hover": {
                        borderColor: "#0D47A1",
                        background: "rgba(21,101,192,0.06)",
                      },
                    }}
                  >
                    Sample Video
                  </Button>
                )}
                {course.hasCourseContent && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<InfoIcon />}
                    onClick={() => setDetailOpen(true)}
                    sx={{
                      color: "#FF9800",
                      borderColor: "#FF9800",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      textTransform: "none",
                      fontSize: "0.8rem",
                      width: "100%",
                      "&:hover": {
                        borderColor: "#F57C00",
                        background: "rgba(255,152,0,0.06)",
                      },
                    }}
                  >
                    Course Details
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Video Dialog */}
      <Dialog
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: "hidden" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 1,
            background: "#000",
          }}
        >
          <IconButton
            onClick={() => setVideoOpen(false)}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0, background: "#000" }}>
          {embedUrl && (
            <Box
              component="iframe"
              src={embedUrl}
              title="Sample lecture video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              sx={{
                width: "100%",
                height: { xs: 250, sm: 350, md: 450 },
                border: "none",
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Course Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: "hidden" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            pt: 2,
            pb: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#1a1a1a",
            }}
          >
            {course.title}
          </Typography>
          <IconButton onClick={() => setDetailOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ px: 3, pb: 3, pt: 1 }}>
          {isLoadingDetail ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Loading course details...
              </Typography>
            </Box>
          ) : detailError ? (
            <Box sx={{ py: 4 }}>
              <Typography variant="body2" color="error" align="center">
                Failed to load course details. Please try again.
              </Typography>
            </Box>
          ) : (
            <>
              {courseDetail.description && (
                <Typography
                  variant="body2"
                  sx={{ color: "#666", mb: 2, lineHeight: 1.6 }}
                >
                  {courseDetail.description}
                </Typography>
              )}
              {courseDetail.courseContent && (
                <RichTextDisplay
                  content={courseDetail.courseContent}
                  sx={{ fontSize: "0.95rem" }}
                />
              )}
              {!courseDetail.courseContent && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  sx={{ py: 3 }}
                >
                  No detailed content available for this course.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CourseCard;
